// Email validation
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone number validation (Kenyan format)
export const isValidPhoneNumber = (phoneNumber) => {
  // Accept various Kenyan formats: +254XXXXXXXXX, 254XXXXXXXXX, 07XXXXXXXX, 7XXXXXXXX
  const phoneRegex = /^(?:\+254|254|0)?[17][0-9]{8}$/;
  return phoneRegex.test(phoneNumber);
};

// Password validation (minimum 6 characters, at least one letter and one number)
export const isValidPassword = (password) => {
  if (password.length < 6) return false;
  const letterRegex = /[a-zA-Z]/;
  const numberRegex = /[0-9]/;
  return letterRegex.test(password) && numberRegex.test(password);
};

// Strong password validation (minimum 8 characters, at least one uppercase letter, one lowercase letter, one number, and one special character)
export const isStrongPassword = (password) => {
  if (password.length < 8) return false;
  const uppercaseRegex = /[A-Z]/;
  const lowercaseRegex = /[a-z]/;
  const numberRegex = /[0-9]/;
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
  
  return uppercaseRegex.test(password) && 
         lowercaseRegex.test(password) && 
         numberRegex.test(password) && 
         specialCharRegex.test(password);
};

// Name validation (letters, spaces, hyphens, apostrophes only)
export const isValidName = (name) => {
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  return nameRegex.test(name) && name.length >= 2;
};

// Required field validation
export const isRequired = (value) => {
  return value && value.trim() !== '';
};

// Date validation (past date only)
export const isPastDate = (dateString) => {
  const selectedDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selectedDate < today;
};

// Date validation (future date only)
export const isFutureDate = (dateString) => {
  const selectedDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selectedDate > today;
};

// Numeric validation
export const isNumeric = (value) => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

// URL validation
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

// Form validation helper
export const validateForm = (formData, validationRules) => {
  const errors = {};
  
  Object.keys(validationRules).forEach((field) => {
    const value = formData[field];
    const rules = validationRules[field];
    
    // Required validation
    if (rules.required && !isRequired(value)) {
      errors[field] = rules.requiredMessage || 'This field is required';
      return;
    }
    
    // Skip other validations if the field is empty and not required
    if (!value && !rules.required) return;
    
    // Email validation
    if (rules.email && value && !isValidEmail(value)) {
      errors[field] = rules.emailMessage || 'Please enter a valid email address';
    }
    
    // Phone validation
    if (rules.phone && value && !isValidPhoneNumber(value)) {
      errors[field] = rules.phoneMessage || 'Please enter a valid phone number';
    }
    
    // Password validation
    if (rules.password && value && !isValidPassword(value)) {
      errors[field] = rules.passwordMessage || 'Password must be at least 6 characters with at least one letter and one number';
    }
    
    // Strong password validation
    if (rules.strongPassword && value && !isStrongPassword(value)) {
      errors[field] = rules.strongPasswordMessage || 'Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character';
    }
    
    // Name validation
    if (rules.name && value && !isValidName(value)) {
      errors[field] = rules.nameMessage || 'Please enter a valid name';
    }
    
    // Custom validation
    if (rules.custom && !rules.custom.validator(value)) {
      errors[field] = rules.custom.message || 'Invalid value';
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
